import React, { Component } from "react";
import * as PropTypes from "prop-types";
import { Checkbox, Col, Form, Modal, Row, Typography } from "antd";
import moment from "moment";

import { timeService } from "../../redux/services";

import {
	UIAvatarLabel,
	UIDrawerForm,
	UIFormSection,
} from "./../../components";

import DayTable from "./table";
import { getTracksSummary, TIME_TYPES, TimeSummary } from "./create";

const CALCULATION_TYPES = {
	flexivel          : "Flexível",
	intervalo_flexivel: "Intervalo Flexível",
	horario           : "Horário",
	normal            : "Normal",
	horista           : "Horista",
};

const formatMinutesLabel = (value) => (value ?? "") !== "" ? `${value} min` : "-";

class Show extends Component {
	static propTypes = {
		visible: PropTypes.bool.isRequired,
		onClose: PropTypes.func.isRequired,
		external: PropTypes.bool,
	};

	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			item: {},
			values: [],
			summary: {
				minutes: 0,
				worked: 0,
				off: 0,
			},
		};
	}

	onOpen = (uuid) => {
		this.setState({
			isLoading: true,
		});

		timeService.show({ uuid })
			.then((response) => {
				const item = response.data.data[0];

				this.setState({
					isLoading: false,
					item: item,
					values: item.horarios_dias_semana,
				});
			})
			.catch((data) => {
				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
					onOk: () => {
						// Force close
						return this.onClose();
					}
				});
			});
	};

	// A tabela avisa por aqui quando termina de montar os dias
	getIntervals = (tracks) => {
		this.setState({ summary: getTracksSummary(tracks) });
	};

	onClose = () => {
		this.setState({
			item: {},
		});

		this.props.onClose();
	};

	render() {
		const { visible } = this.props;
		const { isLoading, item, values, summary } = this.state;

		const isHorista = item.tipo_calculo === "horista";
		const isEscala = item.tipo_horario === "escala";
		const fixarTolerancia = item.tolerancia_clt_art_58 == 1;
		const type = TIME_TYPES[item.tipo_horario];
		const intervalo = item.intervalo_refeicao_sequencias ? item.intervalo_refeicao_sequencias.split("-") : null;

		return (
			<UIDrawerForm
				visible={visible}
				width={1200}
				onClose={this.onClose}
				isLoading={isLoading}
				showBtnSave={false}
				icon={<i className="fal fa-clock" />}
				title="Visualizar horário"
				subtitle="Consulte as informações do horário de trabalho.">
				<Form layout="vertical">
					<UIFormSection
						icon={<i className="fal fa-clipboard-list" />}
						title="Informações gerais"
						description="Identificação e regras de cálculo do horário.">
						<Row gutter={16}>
							<Col xs={24} md={10}>
								<Form.Item label="Descrição">
									{item.descricao ?? "-"}
								</Form.Item>
							</Col>
							<Col xs={24} md={5}>
								<Form.Item label="Status">
									<span className={`status-tag ${item.is_active ? "status-tag-success" : "status-tag-error"}`}>
										<i className="status-tag-dot" />
										{item.is_active ? "Ativo" : "Inativo"}
									</span>
								</Form.Item>
							</Col>
							<Col xs={24} md={9}>
								<Form.Item label="Tipo de cálculo">
									{CALCULATION_TYPES[item.tipo_calculo] ?? "-"}
								</Form.Item>
							</Col>
						</Row>
						{!isHorista ? (
							<>
								<Row gutter={16}>
									<Col xs={24} md={6}>
										<Form.Item label="Hora extra">
											{item.hora_extra?.nome ?? "-"}
										</Form.Item>
									</Col>
									<Col xs={24} md={6}>
										<Form.Item label="DSR">
											{item.dsr?.nome ?? "-"}
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Tipo de horário">
											{type ? (
												<UIAvatarLabel
													name={type.label}
													subtitle={type.description}
													icon={<i className="fal fa-clock" />}
													tone={type.tone}
													square
												/>
											) : "-"}
										</Form.Item>
									</Col>
								</Row>
								{isEscala && (
									<Row gutter={16}>
										<Col xs={24} md={6}>
											<Form.Item label="Tipo de hora escala">
												{item.tipo_hora_escala ?? "-"}
											</Form.Item>
										</Col>
										<Col xs={24} md={6}>
											<Form.Item label="Data base">
												{item.data_inicio_escala ? moment.utc(item.data_inicio_escala, "YYYY-MM-DD").format("DD/MM/YYYY") : "-"}
											</Form.Item>
										</Col>
									</Row>
								)}
							</>
						) : (
							<Row gutter={16}>
								<Col xs={24} md={6}>
									<Form.Item label="Fechamento">
										{item.fechamento ?? "-"}
									</Form.Item>
								</Col>
							</Row>
						)}
						<Row gutter={16}>
							<Col xs={24} md={10}>
								<Form.Item label="Criação">
									{item.created_at ? moment(item.created_at).calendar() : "-"}
								</Form.Item>
							</Col>
							<Col xs={24} md={14}>
								<Form.Item label="Última atualização">
									{item.updated_at ? moment(item.updated_at).calendar() : "-"}
								</Form.Item>
							</Col>
						</Row>
					</UIFormSection>
					{!isHorista && (
						<>
							<UIFormSection
								icon={<i className="fal fa-stopwatch" />}
								title="Tolerâncias"
								description="Tolerâncias diárias de extras e faltas e pré-assinalação do intervalo.">
								<Form.Item>
									<Checkbox disabled checked={fixarTolerancia}>
										Fixar tolerância de extras/faltas em 5 minutos e 10 minutos diários <Typography.Text type="secondary">(CLT - Art. 58)</Typography.Text>
									</Checkbox>
								</Form.Item>
								<Row gutter={16}>
									<Col xs={24} md={8}>
										<Form.Item label="Tolerância extra">
											{formatMinutesLabel(item.tolerancia_extra)}
										</Form.Item>
									</Col>
									<Col xs={24} md={8}>
										<Form.Item label="Tolerância falta">
											{formatMinutesLabel(item.tolerancia_falta)}
										</Form.Item>
									</Col>
									<Col xs={24} md={8}>
										<Form.Item label="Pré assinalar intervalo">
											{intervalo ? `Saída ${intervalo[0]} / Entrada ${intervalo[1]}` : "-"}
										</Form.Item>
									</Col>
								</Row>
								{!fixarTolerancia && (
									<Row gutter={16}>
										<Col xs={24} md={8}>
											<Form.Item label="Tolerância antes">
												{item.tolerancia_batida_extra ?? "-"}
											</Form.Item>
										</Col>
										<Col xs={24} md={8}>
											<Form.Item label="Tolerância depois">
												{item.tolerancia_batida_falta ?? "-"}
											</Form.Item>
										</Col>
									</Row>
								)}
							</UIFormSection>
							<UIFormSection
								icon={<i className="fal fa-calendar-week" />}
								title={isEscala ? "Ciclos da escala" : "Dias da semana"}
								description={isEscala ? "Entradas, saídas e duração de cada ciclo da escala." : "Entradas, saídas e tolerâncias de cada dia da semana."}>
								<DayTable
									showOnly={true}
									getIntervals={this.getIntervals}
									values={values}
									tipo_horario={item.tipo_horario}
									tipo_calculo={item.tipo_calculo}
									fixar_tolerancia={fixarTolerancia}
									tolerancia_extra={item.tolerancia_extra}
									tolerancia_falta={item.tolerancia_falta}
								/>
							</UIFormSection>
							<TimeSummary
								summary={summary}
								isEscala={isEscala}
								toleranciaExtra={item.tolerancia_extra ?? "-"}
								toleranciaFalta={item.tolerancia_falta ?? "-"}
							/>
						</>
					)}
				</Form>
			</UIDrawerForm>
		)
	}
}

export default Show;
